import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, AlertCircle, Mic, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { clearSessionId, clearGuestExamUser, clearExamId, clearGuestId } from "@/utils/storage";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenancePage from "@/components/task2/Task2MaintenancePage";

const MicTest = () => {
  const navigate = useNavigate();
  const [micStatus, setMicStatus] = useState<'checking' | 'detected' | 'not-detected'>('checking');
  const [isPlayingTest, setIsPlayingTest] = useState(false);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);

  // Detect browser for specific instructions
  const getBrowserInstructions = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) {
      return {
        icon: '🔴',
        steps: [
          'Look for the microphone icon 🔴 in your browser\'s address bar',
          'Click on it and select "Allow" or "Always allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    } else if (userAgent.includes('Firefox')) {
      return {
        icon: '🎤',
        steps: [
          'Look for the microphone icon 🎤 in your browser\'s address bar',
          'Click on it and select "Allow" or "Always allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    } else if (userAgent.includes('Safari')) {
      return {
        icon: '🎤',
        steps: [
          'Look for the microphone icon 🎤 in your browser\'s address bar',
          'Click on it and select "Allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    } else {
      return {
        icon: '🎤',
        steps: [
          'Look for the microphone icon in your browser\'s address bar',
          'Click on it and select "Allow" or "Always allow"',
          'Refresh the page or click "Try Again" below'
        ]
      };
    }
  };

  useEffect(() => {
    // Clear any existing session data when user comes to mic test
    // This ensures a fresh session is created when they start the exam
    console.log('🧹 Clearing previous session data...');
    clearSessionId();
    clearGuestExamUser();
    clearExamId();
    clearGuestId();
    
    // Also clear any other exam-related data
    localStorage.removeItem('guestToken');
    localStorage.removeItem('sb-nluntjftnyzlxhatvvks-auth-token');
    
    // Check microphone access with better Chrome compatibility
    const checkMicrophoneAccess = async () => {
      try {
        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.log('getUserMedia not supported');
          setMicStatus('not-detected');
          return;
        }

        console.log('Checking microphone access...');
        
        // For Chrome compatibility, we need to be more explicit
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
        
        console.log('Microphone access granted!');
        setMicStatus('detected');
        
        // Stop the stream immediately to avoid issues
        stream.getTracks().forEach(track => track.stop());
        
      } catch (error: any) {
        console.log('Microphone access error:', error.name, error.message);
        setMicStatus('not-detected');
      }
    };

    // Try to check microphone access on page load
    // Note: Chrome may not show permission prompt without user interaction
    checkMicrophoneAccess();
  }, []);

  const handleTestRecording = async () => {
    try {
      // Stop any previous test audio
      if (testAudioRef.current) {
        try { testAudioRef.current.pause(); } catch {}
        testAudioRef.current = null;
      }
      // Use a short static audio clip to verify playback
      const url = 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/mic-test.mp3';
      const el = new Audio(url);
      try { (el as any).playsInline = true; el.setAttribute('playsinline', 'true'); } catch {}
      el.onended = () => { setIsPlayingTest(false); testAudioRef.current = null; };
      el.onerror = () => { setIsPlayingTest(false); testAudioRef.current = null; console.log('Test audio failed to play'); };
      testAudioRef.current = el;
      setIsPlayingTest(true);
      // If we already created an AudioContext, resume it for good measure
      try {
        const ctx: AudioContext | undefined = (window as any).__appAudioContext;
        if (ctx && ctx.state !== 'running') await ctx.resume();
      } catch {}
      await el.play().then(() => {
        (window as any).__mediaUnlocked = true;
        console.log('Test audio playing');
      }).catch((e) => {
        console.log('Test audio play() blocked or failed:', e?.message || e);
        setIsPlayingTest(false);
      });
    } catch (e) {
      console.log('handleTestRecording error', e);
      setIsPlayingTest(false);
    }
  };

  const handleRetryMicrophone = async () => {
    setMicStatus('checking');
    try {
      console.log('Retrying microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      console.log('Microphone access granted on retry!');
      setMicStatus('detected');
      stream.getTracks().forEach(track => track.stop());
    } catch (error: any) {
      console.log('Retry microphone error:', error.name, error.message);
      setMicStatus('not-detected');
    }
  };

  const handleStartExam = async () => {
    // Unlock audio on user gesture (helps iOS autoplay policies)
    const unlockAudio = async () => {
      try {
        // Resume/create a singleton AudioContext and play a short muted tone
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }
          if (ctx.state !== 'running') {
            await ctx.resume().catch(() => {});
          }
          try {
            const gain = ctx.createGain();
            gain.gain.value = 0.0001; // effectively silent
            gain.connect(ctx.destination);
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.connect(gain);
            osc.start();
            osc.stop(ctx.currentTime + 0.05);
          } catch {}
        }
        // Also unlock HTMLMediaElement playback with a silent data URI
        try {
          const silentMp3 = 'data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
          const el = new Audio();
          el.src = silentMp3;
          el.muted = true;
          try { (el as any).playsInline = true; el.setAttribute('playsinline', 'true'); } catch {}
          // Some browsers need currentTime set to small value
          try { el.currentTime = 0.01; } catch {}
          await el.play().catch(() => {});
          try { el.pause(); el.src = ''; } catch {}
        } catch {}
        ;(window as any).__audioUnlocked = true;
        ;(window as any).__mediaUnlocked = true;
        console.log('Audio unlocked');
      } catch (e) {
        console.log('Audio unlock failed:', e);
      }
    };

    if (isTask2MaintenanceEnabled()) {
      return;
    }

    // Navigate to task-2-rtc-voice
    console.log('Starting exam...');
    try { await unlockAudio(); } finally { navigate('/task-two'); }
  };

  if (isTask2MaintenanceEnabled()) {
    return (
      <Task2MaintenancePage
        subtitle="Your full exam is paused after Task 1 while we restore live voice practice for Task 2. You can return to the dashboard and continue with Task 1, Task 3, or your results."
      />
    );
  }

  return (
    <div className=" bg-white flex justify-center items-center py-24 px-4">
      <div className="max-w-[700px] w-full space-y-8">
        {/* Heading */}
        <div className="text-center">
          <h1 className="text-[32px] font-bold text-[#1C1F26] leading-[42px]">
            Ready to start your exam?
          </h1>
        </div>

        {/* Body Text */}
        <div className="text-center">
          <p className="text-base text-[#1C1F26] leading-[26px] font-normal max-w-[560px] mx-auto">
            Make sure you're in a quiet place and your microphone is working. This will simulate the real TCF Canada speaking exam — no typing, no clicking, just you and your voice.
          </p>
          {micStatus === 'checking' && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg max-w-[560px] mx-auto">
              <p className="text-sm text-blue-800 font-medium">
                🔴 We couldn’t detect your microphone. Please allow mic access in your browser settings.
              </p>
            </div>
          )}
        </div>

        {/* Microphone Status Area */}
        <div className="flex flex-col items-center space-y-4">
          {/* Microphone Icon with Pulse Animation */}
          <div className="relative">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              micStatus === 'detected' ? 'bg-green-100' : 
              micStatus === 'not-detected' ? 'bg-red-100' : 'bg-gray-100'
            }`}>
              <Mic className={`w-7 h-7 transition-colors ${
                micStatus === 'detected' ? 'text-green-600' : 
                micStatus === 'not-detected' ? 'text-red-600' : 'text-[#8B7AF5]'
              }`} />
            </div>
            {/* Pulsing circle animation - only show when detected or checking */}
            {micStatus !== 'not-detected' && (
              <>
                <div className="absolute inset-0 rounded-full border-2 border-[#8B7AF5] animate-ping opacity-20"></div>
                <div className="absolute inset-0 rounded-full border-2 border-[#8B7AF5] animate-ping opacity-10" style={{ animationDelay: '0.5s' }}></div>
              </>
            )}
          </div>

          {/* Status Message */}
          <div className="mt-4">
            {micStatus === 'checking' && (
              <div className="flex items-center space-x-2 text-gray-600">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium">Checking microphone...</span>
              </div>
            )}
            
            {micStatus === 'detected' && (
              <div className="flex items-center space-x-2 text-[#0E9F6E]">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Microphone is detected and ready to use.</span>
              </div>
            )}
            
            {micStatus === 'not-detected' && (
              <div className="flex flex-col items-center space-y-3">
                <div className="flex items-center space-x-2 text-[#EF4444]">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">We couldn't detect your microphone. Please allow mic access in your browser settings.</span>
                </div>
                <Button 
                  onClick={handleRetryMicrophone}
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                >
                  Try Again
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Exam Tips Card */}
        <Card className="bg-[#F8F9FB] border-0 max-w-[560px] mx-auto rounded-xl">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-[#0E9F6E] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#1C1F26] leading-[22px]">You'll be given instructions before each task</span>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-[#0E9F6E] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#1C1F26] leading-[22px]">Your answers will be recorded and analyzed for feedback</span>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-[#0E9F6E] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#1C1F26] leading-[22px]">You can speak freely without needing to click anything</span>
            </div>
          </CardContent>
        </Card>

        {/* Test Recording Button */}
        <div className="text-center">
          <button
            onClick={handleTestRecording}
            disabled={isPlayingTest}
            className="text-sm font-medium text-[#1C1F26] hover:underline flex items-center space-x-1 mx-auto disabled:opacity-50"
          >
            {isPlayingTest ? (
              <>
                <div className="w-4 h-4 border-2 border-[#1C1F26] border-t-transparent rounded-full animate-spin"></div>
                <span>Playing test recording...</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span>Play back a test recording</span>
              </>
            )}
          </button>
        </div>

        {/* Start Exam Button */}
        <div className="text-center pt-4">
          <Button
            onClick={handleStartExam}
            disabled={micStatus === 'not-detected' || isTask2MaintenanceEnabled()}
            className="bg-blue-600 hover:bg-blue-600 text-white font-bold text-base px-7 py-4 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontSize: '16px',
              fontWeight: '700',
              padding: '16px 28px',
              borderRadius: '12px'
            }}
          >
            {isTask2MaintenanceEnabled() ? 'Temporarily unavailable' : 'Start My Exam'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MicTest;
